var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from 'axios';
import { AuthenticationMethodType, AuthenticationReason, AuthenticationResult, AuthenticationStatus, AuthenticationSubject, VerificationType } from '@mc-alberta/types';
import { Adapter } from '../Adapter';
import { ApiService } from '../../services/api-service/api-service';
import { AuthenticationStrategy } from '../../types/enums/AuthenticationStrategy';
import { ConsumerIdentityTypeEmvcoVersionPointNine } from '../../utils/consumer-identity/models/consumer-identity.enum';
import { ConsumerVerificationEntity, ConsumerVerificationEvents, ConsumerVerificationMethods, ConsumerVerificationResults } from '../../types/enums/consumer-subject.enum';
import { DEFAULT_IDENTITY_PROVIDER } from '../../utils/consumer-identity/models/consumer-identity.const';
import { HttpHeader } from '../../types/enums/http-header.enum';
import { KpiOperation } from '../../types/enums/KpiOperation';
import { LifecycleAwaitable } from '../../decorators/LifecycleAwaiter';
import { ServiceErrorCode, ServiceErrorReason } from '../../types/enums/errors.enum';
import merge from 'lodash.merge';
import { getAppInstance } from '../../utils/app-instance';
import { ComplianceSettingsUtils } from '../../utils/compliance-settings';
export class ConsumerOtpAdapter extends Adapter {
    constructor(strategyContext) {
        super(strategyContext);
        this.strategyContext = strategyContext;
        this.strategy = AuthenticationStrategy.CONSUMER_OTP;
        this.supportedReasons = [AuthenticationReason.CONSUMER_IDENTITY_VALIDATION];
        this.verificationMethods = [
            ConsumerVerificationMethods.EMAIL_OTP,
            ConsumerVerificationMethods.SMS_OTP
        ];
        this.initializeSession = (params) => __awaiter(this, void 0, void 0, function* () {
            try {
                this.kpi.start(KpiOperation.INITIATE_AUTHENTICATION);
                let requestedValidationChannel;
                /**
                 * Use the requested auth method for OTP if provided, otherwise precheckout will pick one
                 * later on their own.
                 */
                if (params.authenticationMethod) {
                    const { authenticationMethodType, authenticationCredentialReference } = params.authenticationMethod;
                    requestedValidationChannel = {
                        identityProvider: DEFAULT_IDENTITY_PROVIDER,
                        identityType: authenticationMethodType === AuthenticationMethodType.SMS_OTP
                            ? ConsumerIdentityTypeEmvcoVersionPointNine.SMS
                            : ConsumerIdentityTypeEmvcoVersionPointNine.EMAIL,
                        maskedValidationChannel: authenticationCredentialReference
                    };
                }
                const nudetectWidgetData = yield this.getNudetectWidgetData();
                const { data } = yield ApiService.precheckoutIdentitiesVerification(Object.assign(Object.assign({}, (nudetectWidgetData && { nudetectWidgetData })), { accountReference: this.strategyContext.accountReference, authenticatePayload: this.strategyContext, idLookupSessionId: params.authorization, requestedValidationChannel }));
                /**
                 * This is needed to protect us from a bug from Precheckout where they'll give us a 200, but
                 * no idVerificationId.
                 */
                if (!(data === null || data === void 0 ? void 0 : data.idVerificationId)) {
                    return this.destroySession();
                }
                this.authenticationMethods = this.buildAuthenticationMethods(data);
                this.authenticationSessionId = data.idVerificationId;
                this.authenticationStatus = AuthenticationStatus.PENDING_CHALLENGE;
                this.selectedMethodIndex = this.authenticationMethods.findIndex((authMethod) => {
                    return authMethod.authenticationCredentialReference === data.maskedValidationChannel;
                });
            }
            catch (error) {
                return this.destroySession(error);
            }
            return this.getSession();
        });
    }
    preloadSession() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.getSession();
        });
    }
    provideChallenge(otpValue) {
        this.kpi.start(KpiOperation.PROVIDE_AUTHENTICATION_CHALLENGE);
        this.otpValue = otpValue;
        this.authenticationStatus = AuthenticationStatus.PENDING;
        return this.getSession();
    }
    validateSession() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.kpi.start(KpiOperation.COMPLETE_AUTHENTICATION);
            try {
                const { complianceSettings } = ((_a = this.authenticationMethod) === null || _a === void 0 ? void 0 : _a.methodAttributes) || {};
                const shouldRememberUser = ComplianceSettingsUtils.hasRememberMeComplianceResource(complianceSettings);
                const nudetectWidgetData = yield this.getNudetectWidgetData();
                const { data, headers } = yield ApiService.precheckoutIdentitiesValidation(Object.assign(Object.assign(Object.assign({}, (shouldRememberUser && {
                    appInstance: getAppInstance(),
                    complianceSettings
                })), (nudetectWidgetData && { nudetectWidgetData })), { authenticatePayload: this.strategyContext, idVerificationId: this.authenticationSessionId, validationData: this.otpValue }));
                this.assuranceData = this.deriveAssuranceData(data.authorization);
                this.authenticationResult = AuthenticationResult.AUTHENTICATED;
                this.authenticationStatus = AuthenticationStatus.COMPLETE;
                this.isIdentityVerificationPerformed = true;
                const recognitionToken = headers[HttpHeader.SRC_RECOGNITION_TOKEN];
                if (recognitionToken) {
                    this.setOutputMethodAttributes({ recognitionToken });
                }
            }
            catch (error) {
                if (axios.isAxiosError(error)) {
                    return this.handleXhrError(error);
                }
                return this.destroySession(error);
            }
            return this.getSession();
        });
    }
    /**
     * Build authentication methods from the data we receive from backend.
     */
    buildAuthenticationMethods(data) {
        var _a;
        let authenticationMethods;
        if (((_a = data.supportedValidationChannels) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            authenticationMethods = data.supportedValidationChannels.map((channel) => {
                return this.getConsumerAuthMethod({
                    isIdentityTypeEmail: channel.identityType === ConsumerIdentityTypeEmvcoVersionPointNine.EMAIL,
                    maskedValidationChannel: channel.maskedValidationChannel
                });
            });
        }
        else {
            authenticationMethods = [
                this.getConsumerAuthMethod({
                    // TODO: Make this more robust once we find out how we can differentiate this
                    isIdentityTypeEmail: data.maskedValidationChannel.includes('@'),
                    maskedValidationChannel: data.maskedValidationChannel
                })
            ];
        }
        return authenticationMethods;
    }
    /**
     * Build our own consumer auth method until backend gives this to us.
     */
    getConsumerAuthMethod({ isIdentityTypeEmail, maskedValidationChannel }) {
        return {
            authenticationMethodType: isIdentityTypeEmail
                ? AuthenticationMethodType.EMAIL_OTP
                : AuthenticationMethodType.SMS_OTP,
            authenticationCredentialReference: maskedValidationChannel,
            authenticationSubject: AuthenticationSubject.CONSUMER
        };
    }
    /**
     * Derive our own assurance data as a stop-gap until backend gives this to us normally.
     */
    deriveAssuranceData(authorization) {
        return {
            verificationData: [
                {
                    verificationEntity: ConsumerVerificationEntity.SRCPI,
                    verificationMethod: ConsumerVerificationMethods.EMAIL_OTP,
                    verificationResults: ConsumerVerificationResults.VERIFIED,
                    verificationTimestamp: Date.now().toString(),
                    verificationType: VerificationType.CONSUMER,
                    verificationEvents: [ConsumerVerificationEvents.SRC_PROFILE_ACCESS],
                    additionalData: authorization
                }
            ]
        };
    }
    handleXhrError(error) {
        var _a, _b, _c, _d;
        const errorCode = (_a = error.response) === null || _a === void 0 ? void 0 : _a.status;
        const errorReason = (_c = (_b = error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.status;
        /**
         * If the user entered the OTP incorrectly.
         */
        if (errorCode === ServiceErrorCode.BAD_REQUEST &&
            errorReason === ServiceErrorReason.INVALID_STATE) {
            this.authenticationStatus = AuthenticationStatus.PENDING_CHALLENGE;
            return this.getSession();
        }
        /**
         * If the user entered the OTP incorrectly too many times.
         */
        if (errorCode === ServiceErrorCode.FORBIDDEN) {
            this.authenticationStatus = AuthenticationStatus.CHALLENGE_FAILED;
            return this.getSession();
        }
        /**
         * If the session expired before OTP completed, reinitialize the session.
         */
        if (errorCode === ServiceErrorCode.UNAUTHORIZED) {
            return this.initializeSession({
                authenticationSubject: this.authenticationMethod.authenticationSubject,
                authenticationReason: AuthenticationReason.CONSUMER_IDENTITY_VALIDATION,
                authorization: this.authorization || ((_d = error.response) === null || _d === void 0 ? void 0 : _d.headers[HttpHeader.WWW_AUTHENTICATE])
            });
        }
        return this.destroySession(error);
    }
    setMethodAttributes(payload = {}) {
        this.authenticationMethod.methodAttributes = merge(this.authenticationMethod.methodAttributes, payload);
    }
}
__decorate([
    LifecycleAwaitable,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], ConsumerOtpAdapter.prototype, "preloadSession", null);
